import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toast } from 'sonner';
import { API_BASE } from '../../../config/api';
import ProgrammeThumbnailCard from '../ProgrammeThumbnailCard';

const API = `${API_BASE}/api`;

export default function ProgrammeInfoStep({ programmeId, programme, token, headers, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [metadata, setMetadata] = useState({
    programme_name: '',
    professional_body: '',
    designation: '',
    occupation: '',
    nqf_level: '',
    target_audience: '',
    entry_requirements: '',
    duration: '',
    credits: '',
    facilitator_name: '',
    moderator_name: '',
    start_date: '',
    end_date: '',
    structure_type: 'weeks',
    description: '',
    is_published: false
  });

  useEffect(() => {
    if (programme) {
      setMetadata({
        programme_name: programme.programme_name || '',
        professional_body: programme.professional_body || '',
        designation: programme.designation || '',
        occupation: programme.occupation || '',
        nqf_level: programme.nqf_level || '',
        target_audience: programme.target_audience || '',
        entry_requirements: programme.entry_requirements || '',
        duration: programme.duration || '',
        credits: programme.credits || '',
        facilitator_name: programme.facilitator_name || '',
        moderator_name: programme.moderator_name || '',
        start_date: programme.start_date || '',
        end_date: programme.end_date || '',
        structure_type: programme.structure_type || 'weeks',
        description: programme.description || '',
        is_published: programme.is_published || false
      });
    }
  }, [programme]);

  const handleChange = (field, value) => {
    setMetadata(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (programmeId) {
        await axios.put(`${API}/programmes/${programmeId}`, metadata, { headers });
        toast.success('Programme updated');
      } else {
        const res = await axios.post(`${API}/programmes`, metadata, { headers });
        toast.success('Programme created');
        // Redirect to edit mode
        window.location.href = `/admin/programmes/${res.data.programme_id}/workflow?step=1`;
      }
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <ProgrammeThumbnailCard key={programmeId || 'new'} programmeId={programmeId} thumbnailUrl={programme?.thumbnail_url} headers={headers}
        onChange={(url) => { if (programme) programme.thumbnail_url = url; /* keep parent copy fresh without resetting unsaved form edits */ }} />
      {/* Programme Name */}
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Label className="text-zinc-300">Programme Name *</Label>
          <Input
            value={metadata.programme_name}
            onChange={(e) => handleChange('programme_name', e.target.value)}
            placeholder="e.g., Certified Digital Practitioner in Emerging Technologies"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Professional Body</Label>
          <Input
            value={metadata.professional_body}
            onChange={(e) => handleChange('professional_body', e.target.value)}
            placeholder="e.g., ICITP"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Designation</Label>
          <Input
            value={metadata.designation}
            onChange={(e) => handleChange('designation', e.target.value)}
            placeholder="e.g., CDP: AI"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Occupation</Label>
          <Input
            value={metadata.occupation}
            onChange={(e) => handleChange('occupation', e.target.value)}
            placeholder="e.g., Compliance Manager"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">NQF Level</Label>
          <Select
            value={metadata.nqf_level}
            onValueChange={(value) => handleChange('nqf_level', value)}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
              <SelectValue placeholder="Select NQF Level" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {[1,2,3,4,5,6,7,8,9,10].map(level => (
                <SelectItem key={level} value={level.toString()}>NQF {level}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-zinc-300">Target Audience</Label>
          <Input
            value={metadata.target_audience}
            onChange={(e) => handleChange('target_audience', e.target.value)}
            placeholder="e.g., Compliance professionals, Risk managers"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Entry Requirements</Label>
          <Input
            value={metadata.entry_requirements}
            onChange={(e) => handleChange('entry_requirements', e.target.value)}
            placeholder="e.g., Bachelor's degree or 3 years experience"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Duration</Label>
          <Input
            value={metadata.duration}
            onChange={(e) => handleChange('duration', e.target.value)}
            placeholder="e.g., 20 weeks"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Credits</Label>
          <Input
            value={metadata.credits}
            onChange={(e) => handleChange('credits', e.target.value)}
            placeholder="e.g., 120"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Facilitator Name</Label>
          <Input
            value={metadata.facilitator_name}
            onChange={(e) => handleChange('facilitator_name', e.target.value)}
            placeholder="Facilitator name"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Moderator Name</Label>
          <Input
            value={metadata.moderator_name}
            onChange={(e) => handleChange('moderator_name', e.target.value)}
            placeholder="Moderator name"
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Start Date</Label>
          <Input
            type="date"
            value={metadata.start_date}
            onChange={(e) => handleChange('start_date', e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">End Date</Label>
          <Input
            type="date"
            value={metadata.end_date}
            onChange={(e) => handleChange('end_date', e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-300">Structure Type</Label>
          <Select
            value={metadata.structure_type}
            onValueChange={(value) => handleChange('structure_type', value)}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="weeks">Weekly Structure</SelectItem>
              <SelectItem value="months">Monthly Structure</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3 pt-6">
          <Switch
            checked={metadata.is_published}
            onCheckedChange={(checked) => handleChange('is_published', checked)}
          />
          <Label className="text-zinc-300">Published</Label>
        </div>

        <div className="col-span-2">
          <Label className="text-zinc-300">Description</Label>
          <Textarea
            value={metadata.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Programme description and overview..."
            rows={4}
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-zinc-800">
        <Button
          onClick={handleSave}
          disabled={saving || !metadata.programme_name}
          className="bg-teal-600 hover:bg-teal-700 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Programme Info
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
